import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import styles from "./EstimateLayout.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetEstimateLayouts, handleCreateEstimateLayout as createEstimateLayoutThunk } from "~/thunks/estimate-setting/estimateSettingThunk";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import EstimateLayoutStep1, { type EstimateLayoutStep1Ref } from "./steps/step-1/EstimateLayoutStep1";
import EstimateLayoutStep2 from "./steps/step-2/EstimateLayoutStep2";
import type { EstimateLayoutStep1FormData, EstimateLayoutStep2FormData, EstimateLayoutStep3FormData, EstimateLayoutStep4FormData, EstimateLayoutStep5FormData, EstimateLayoutStep6FormData, EstimateLayoutStep7FormData } from "~/utils/interface/estimateSetting";
import EstimateLayoutStep3 from "./steps/step-3/EstimateLayoutStep3";
import EstimateLayoutStep4 from "./steps/step-4/EstimateLayoutStep4";
import EstimateLayoutStep5 from "./steps/step-5/EstimateLayoutStep5";
import EstimateLayoutStep6 from "./steps/step-6/EstimateLayoutStep6";
import EstimateLayoutStep7 from "./steps/step-7/EstimateLayoutStep7";
import EstimatePDF from "~/pages/admin/jobs/job-detail/tabs/estimate/edit/viewPDF/EstimatePDF";
import { notify } from "~/utils/helper";

dayjs.extend(relativeTime);
const cx = classNames.bind(styles);

interface EstimateLayoutProps {
    isAddEstimateLayout: boolean;
    setIsAddEstimateLayout: (isAddEstimateLayout: boolean) => void;
    showModalCreateEstimateLayout: boolean;
    setShowModalCreateEstimateLayout: (showModalCreateEstimateLayout: boolean) => void;
    onLayoutCreatedView?: (isViewingCreatedPdf: boolean) => void;
}

export type EstimateLayoutRef = {
    continueFromStep1: () => void;
    handleContinue: () => void;
};

const STEP_CONTENT = [
    {
        id: 1,
        title: 'Step 1',
        description: 'Title',
    },
    {
        id: 2,
        title: 'Step 2',
        description: 'About Us',
    },
    {
        id: 3,
        title: 'Step 3',
        description: 'Inspection',
    },
    {
        id: 4,
        title: 'Step 4',
        description: 'Quote Details',
    },
    {
        id: 5,
        title: 'Step 5',
        description: 'Authorization',
    },
    {
        id: 6,
        title: 'Step 6',
        description: 'Terms & Condition',
    },
    {
        id: 7,
        title: 'Step 7',
        description: 'CBR Insurance',
    },
];

const EstimateLayout = forwardRef<EstimateLayoutRef, EstimateLayoutProps>(({ isAddEstimateLayout, setIsAddEstimateLayout, showModalCreateEstimateLayout, setShowModalCreateEstimateLayout, onLayoutCreatedView }, ref) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const { estimateLayouts, needRefreshData } = useAppSelector((state) => state.estimateSetting);
    const [formData, setFormData] = useState({
        estimateLayoutName: '',
        location: '',
    });
    const [error, setError] = useState({
        estimateLayoutName: '',
        location: '',
    });
    const [selectedStep, setSelectedStep] = useState(1);
    const [step1Data, setStep1Data] = useState<EstimateLayoutStep1FormData | null>(null);
    const step1Ref = useRef<EstimateLayoutStep1Ref>(null);
    const [step2Data, setStep2Data] = useState<EstimateLayoutStep2FormData | null>(null);
    const [step3Data, setStep3Data] = useState<EstimateLayoutStep3FormData | null>(null);
    const [step4Data, setStep4Data] = useState<EstimateLayoutStep4FormData | null>(null);
    const [step5Data, setStep5Data] = useState<EstimateLayoutStep5FormData | null>(null);
    const [step6Data, setStep6Data] = useState<EstimateLayoutStep6FormData | null>(null);
    const [step7Data, setStep7Data] = useState<EstimateLayoutStep7FormData | null>(null);
    const [isViewLayoutPdf, setViewLayoutPdf] = useState(false);
    const [isLayoutJustCreated, setLayoutJustCreated] = useState(false);
    const layoutPdfRef = useRef<{ exportPdf: () => void } | null>(null);

    useEffect(() => {
        setLoading(true);
        dispatch(handleGetEstimateLayouts()).then(() => {
            setLoading(false);
        });
    }, [dispatch, needRefreshData]);

    useEffect(() => {
        if (!isAddEstimateLayout) {
            setSelectedStep(1);
            setStep1Data(null);
            setStep2Data(null);
            setStep3Data(null);
            setStep4Data(null);
            setStep5Data(null);
            setStep6Data(null);
            setStep7Data(null);
            setViewLayoutPdf(false);
            setLayoutJustCreated(false);
            onLayoutCreatedView?.(false);
        }
    }, [isAddEstimateLayout, onLayoutCreatedView]);

    const handleChange = (name: string, value: string) => {
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        const newError = {
            estimateLayoutName: '',
            location: '',
        };
        if (!formData.estimateLayoutName) {
            newError.estimateLayoutName = 'Estimate Layout Name is required';
        }
        if (!formData.location) {
            newError.location = 'Location is required';
        }
        setError(newError);
        return Object.values(newError).every((v) => v === '');
    };

    const handleCloseModalCreateEstimateLayout = () => {
        setShowModalCreateEstimateLayout(false);
        setFormData({
            estimateLayoutName: '',
            location: '',
        });
        setError({
            estimateLayoutName: '',
            location: '',
        });
    };


    const handleCreateEstimateLayout = () => {
        if (!validateForm()) {
            return;
        }

        setIsAddEstimateLayout(true);
        setShowModalCreateEstimateLayout(false);
    };

    const handleSelectStep = (stepId: number) => {
        if (selectedStep === 1 && stepId > 1) {
            const isValid = step1Ref.current?.validate?.();
            if (!isValid) return;
        }
        setSelectedStep(stepId);
    };

    const handleContinueFromStep1 = useCallback(() => {
        const isValid = step1Ref.current?.validate?.();
        if (isValid) {
            setSelectedStep(2);
        }
    }, []);

    const getAggregatedLayoutFormData = useCallback((): Record<string, unknown> => {
        const quotes =
            step4Data?.owens?.map((owen) => ({
                title: owen.title,
                isVisible: true,
                sectionItems:
                    owen.sections?.flatMap((sec) =>
                        sec.lines?.map((line) => ({ itemName: line.itemName })) ?? []
                    ) ?? [],
            })) ?? [];
        return {
            name: formData.estimateLayoutName || step1Data?.titleStep1,
            companyName: step1Data?.company,
            titleFileUrl: step1Data?.fileUrl,
            firstName: step1Data?.firstName,
            lastName: step1Data?.lastName,
            address: step1Data?.address,
            stateProvince: step1Data?.province,
            city: step1Data?.city,
            province: step1Data?.province,
            zipCodePostalCode: step1Data?.code,
            estimateDate: step1Data?.date ?? new Date(),
            usDescription: step2Data?.description ?? '',
            quotes,
            optional: step5Data?.optional ?? { title: '', isVisible: true, sections: [] },
            termsNConditions: step6Data?.termsNConditions ?? '',
            insuranceDetails: step7Data?.insuranceDetails ?? '',
        };
    }, [formData.estimateLayoutName, step1Data, step2Data, step4Data, step5Data, step6Data, step7Data]);


    const handleContinueFromStep7 = useCallback(async () => {
        const payload = new FormData();
        payload.append("titleStep1", step1Data?.titleStep1 || "");
        payload.append("name", step1Data?.name || "");
        payload.append("estimateDate", step1Data?.date ? dayjs(step1Data.date).format("YYYY-MM-DD") : "");
        if (step1Data?.file) {
            payload.append("primaryImage", step1Data.file);
        }
        if (step1Data?.certificateLogo?.file) {
            payload.append("certificationSecondaryLogo", step1Data.certificateLogo.file);
        }
        payload.append("companyName", step1Data?.company || "");
        payload.append("firstName", step1Data?.firstName || "");
        payload.append("lastName", step1Data?.lastName || "");
        payload.append("address", step1Data?.address || "");
        payload.append("city", step1Data?.city || "");
        payload.append("stateProvince", step1Data?.province || "");
        payload.append("zipCodePostalCode", step1Data?.code || "");
        payload.append("titleStep2", step2Data?.titleStep2 || "");
        payload.append("aboutUs", step2Data?.description || "");
        payload.append("titleStep3", step3Data?.titleStep3 || "");
        const sectionsStep3 = step3Data?.sections ?? [];
        if (sectionsStep3.length === 0) {
            payload.append("inspections[0][sectionTitle]", "");
            payload.append("inspections[0][style]", "0");
            payload.append("inspections[0][images][0][description]", "");
            payload.append("inspections[0][images][0][sortOrder]", "0");
        } else {
            sectionsStep3.forEach((section, sectionIndex) => {
                payload.append(`inspections[${sectionIndex}][sectionTitle]`, section.sectionTitle ?? "");
                payload.append(`inspections[${sectionIndex}][style]`, "0");
                (section.lines ?? []).forEach((line, imageIndex) => {
                    payload.append(`inspections[${sectionIndex}][images][${imageIndex}][description]`, line.lineDescription ?? "");
                    payload.append(`inspections[${sectionIndex}][images][${imageIndex}][sortOrder]`, String(imageIndex));
                    if (line.lineImage) {
                        payload.append(`inspectionImage_${sectionIndex}_${imageIndex}`, line.lineImage);
                    }
                });
            });
        }
        payload.append("titleStep4", step4Data?.titleStep4 || "");

        const owensStep4 = step4Data?.owens ?? [];
        if (owensStep4.length === 0) {
            payload.append("quotes[0][quoteName]", "");
            payload.append("quotes[0][sortOrder]", "0");
            payload.append("quotes[0][note]", "");
            payload.append("quotes[0][quoteSections][0][title]", "");
            payload.append("quotes[0][quoteSections][0][isVisible]", "true");
            payload.append("quotes[0][quoteSections][0][sortOrder]", "0");
            payload.append("quotes[0][quoteSections][0][sectionItems][0][itemName]", "");
            payload.append("quotes[0][quoteSections][0][sectionItems][0][sortOrder]", "0");
            payload.append("quotes[0][quoteSections][0][sectionItems][0][type]", "MATERIAL");
            payload.append("quotes[0][quoteSections][0][sectionItems][0][uom]", "");
            payload.append("quotes[0][quoteSections][0][sectionItems][0][quantity]", "0");
            payload.append("quotes[0][quoteSections][0][sectionItems][0][unitPrice]", "");
        } else {
            owensStep4.forEach((quote, quoteIndex) => {
                payload.append(`quotes[${quoteIndex}][quoteName]`, quote.title ?? "");
                payload.append(`quotes[${quoteIndex}][sortOrder]`, String(quoteIndex));
                payload.append(`quotes[${quoteIndex}][note]`, quote.note ?? "");
                payload.append(`quotes[${quoteIndex}][quoteProfitMargin]`, String(quote.quoteProfitMargin ?? 0));
                (quote.sections ?? []).forEach((section, sectionIndex) => {
                    payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][title]`, section.title ?? "");
                    payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][isVisible]`, "true");
                    payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][sortOrder]`, String(sectionIndex));
                    (section.lines ?? []).forEach((item, itemIndex) => {
                        payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][sectionItems][${itemIndex}][itemName]`, item.itemName ?? "");
                        payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][sectionItems][${itemIndex}][sortOrder]`, String(itemIndex));
                        payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][sectionItems][${itemIndex}][type]`, item.type ?? "MATERIAL");
                        payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][sectionItems][${itemIndex}][uom]`, item.uom ?? "");
                        payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][sectionItems][${itemIndex}][quantity]`, String(item.quantity ?? ""));
                        payload.append(`quotes[${quoteIndex}][quoteSections][${sectionIndex}][sectionItems][${itemIndex}][unitPrice]`, String(item.price ?? ""));
                    });
                });
            });
        }
        payload.append("titleStep5", step5Data?.titleStep5 || "");
        payload.append("disclaimer", step5Data?.disclaimer || "");
        payload.append("optionalUpdates[title]", step5Data?.optional?.title ?? "");
        const optionalSections = step5Data?.optional?.sections ?? [];
        if (optionalSections.length === 0) {
            payload.append("optionalUpdates[sectionItems][0][itemName]", "");
            payload.append("optionalUpdates[sectionItems][0][sortOrder]", "0");
            payload.append("optionalUpdates[sectionItems][0][type]", "MATERIAL");
            payload.append("optionalUpdates[sectionItems][0][uom]", "");
            payload.append("optionalUpdates[sectionItems][0][quantity]", "0");
            payload.append("optionalUpdates[sectionItems][0][unitPrice]", "");
        } else {
            optionalSections.forEach((section, sectionIndex) => {
                payload.append(`optionalUpdates[sectionItems][${sectionIndex}][itemName]`, section.itemName ?? "");
                payload.append(`optionalUpdates[sectionItems][${sectionIndex}][sortOrder]`, String(sectionIndex));
                payload.append(`optionalUpdates[sectionItems][${sectionIndex}][type]`, section.type ?? "MATERIAL");
                payload.append(`optionalUpdates[sectionItems][${sectionIndex}][uom]`, section.uom ?? "");
                payload.append(`optionalUpdates[sectionItems][${sectionIndex}][quantity]`, String(section.quantity ?? ""));
                payload.append(`optionalUpdates[sectionItems][${sectionIndex}][unitPrice]`, String(section.unitPrice ?? ""));
            });
        }
        payload.append("isDeposit", step5Data?.isDeposit ? "true" : "false");
        payload.append("signerLayouts[0][firstName]", step5Data?.signer?.firstName ?? "");
        payload.append("signerLayouts[0][lastName]", step5Data?.signer?.lastName ?? "");
        payload.append("signerLayouts[0][email]", step5Data?.signer?.email ?? "");
        payload.append("productionSelection[title]", step5Data?.selection?.title ?? "");
        const selectionsStep5 = step5Data?.selection?.selections ?? [];
        if (selectionsStep5.length === 0) {
            payload.append("productionSelection[selections][0][fieldName]", "");
            payload.append("productionSelection[selections][0][customerInput]", "");
            payload.append("productionSelection[selections][0][sortOrder]", "0");
        } else {
            selectionsStep5.forEach((selection, selectionIndex) => {
                payload.append(`productionSelection[selections][${selectionIndex}][fieldName]`, selection.name ?? "");
                payload.append(`productionSelection[selections][${selectionIndex}][customerInput]`, selection.customer ?? "");
                payload.append(`productionSelection[selections][${selectionIndex}][sortOrder]`, String(selectionIndex));
            });
        }
        payload.append("footerNotes", step5Data?.footerNotes ?? "");
        payload.append("titleStep6", step6Data?.titleStep6 || "");
        payload.append("termsNConditions", step6Data?.termsNConditions || "");
        payload.append("titleStep7", step7Data?.titleStep7 || "");
        payload.append("insuranceDetails", step7Data?.insuranceDetails || "");
        payload.append("isAcknowledged", step7Data?.isAcknowledged ? "true" : "false");
        payload.append("estimateLayoutName", formData.estimateLayoutName || "");
        payload.append("location", formData.location || "Christian Brother Roofing");
        payload.append("lastStep", "STEP_7_CBR_INSURANCE");
        payload.append("status", "DRAFT");

        try {
            setLoading(true);
            const result = await dispatch(createEstimateLayoutThunk(payload));
            if (createEstimateLayoutThunk.fulfilled.match(result)) {
                setViewLayoutPdf(true);
                setLayoutJustCreated(true);
                onLayoutCreatedView?.(true);
            }
        } catch {
            notify('error', 'Failed to create estimate layout');

        } finally {
            setLoading(false);
        }
    }, [dispatch, step1Data, step2Data, step3Data, step4Data, step5Data, step6Data, step7Data, formData.estimateLayoutName, formData.location, onLayoutCreatedView]);

    const handleContinue = useCallback(() => {
        if (selectedStep === 1) {
            handleContinueFromStep1();
        } else if (selectedStep === 7) {
            handleContinueFromStep7();
        } else if (selectedStep >= 2 && selectedStep < 7) {
            setSelectedStep(selectedStep + 1);
        }
    }, [selectedStep, handleContinueFromStep1, handleContinueFromStep7]);

    useImperativeHandle(ref, () => ({
        continueFromStep1: handleContinueFromStep1,
        handleContinue,
    }), [handleContinueFromStep1, handleContinue]);

    const renderStepContent = () => {
        switch (selectedStep) {
            case 1:
                return (
                    <EstimateLayoutStep1
                        ref={step1Ref}
                        onStep1DataChange={setStep1Data}
                        initialData={step1Data}
                    />
                );
            case 2:
                return <EstimateLayoutStep2 onStep2DataChange={setStep2Data} initialData={step2Data} />;
            case 3:
                return <EstimateLayoutStep3 onStep3DataChange={setStep3Data} initialData={step3Data} />;
            case 4:
                return <EstimateLayoutStep4 onStep4DataChange={setStep4Data} initialData={step4Data} />;
            case 5:
                return (
                    <EstimateLayoutStep5
                        onStep5DataChange={(data) => {
                            setStep5Data(data);
                        }}
                        initialData={step5Data}
                    />
                );
            case 6:
                return <EstimateLayoutStep6 onStep6DataChange={setStep6Data} initialData={step6Data} />;
            case 7:
                return (
                    <EstimateLayoutStep7
                        onStep7DataChange={setStep7Data}
                        initialData={step7Data}
                        onPreview={() => setViewLayoutPdf(true)}
                    />
                );
            default:
                return (
                    <EstimateLayoutStep1
                        ref={step1Ref}
                        onStep1DataChange={setStep1Data}
                        initialData={step1Data}
                    />
                );
        }
    };

    return (
        <div className={cx('estimate-layout-container')}>
            {loading ? (
                <div className={cx('loading-container')}>
                    <LoadingMainContent />
                </div>
            ) : (
                <>
                    {isAddEstimateLayout ? (
                        <div className={cx('add-estimate-layout-container')}>
                            {!isLayoutJustCreated && (
                                <div className={cx('step-container')}>
                                    <div className={cx('title-editor-wrap')}>
                                        <TitleEditor
                                            value={formData.estimateLayoutName}
                                            name="estimateLayoutName"
                                            isViewMode={false}
                                            placeholder="Enter Estimate Layout Name"
                                            inputkey="estimateLayoutName"
                                            onChange={(value) => {
                                                handleChange('estimateLayoutName', value);
                                            }}
                                        />
                                    </div>

                                    <div className={cx('step-content')}>
                                        {STEP_CONTENT.map((step) => (
                                            <button className={cx('step-content-item', { selected: selectedStep === step.id })} key={step.id} onClick={() => handleSelectStep(step.id)}>
                                                <div className={cx('step-content-item-number', { selected: selectedStep === step.id })}>{step.id}</div>
                                                <div className={cx('step-content-info')}>
                                                    <h3 className={cx('step-content-item-title')}>{step.title}</h3>
                                                    <p className={cx('step-content-item-description', { selected: selectedStep === step.id })}>{step.description}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className={cx('estimate-layout-content', { 'full-width': isLayoutJustCreated })}>
                                {isViewLayoutPdf ? (
                                    <>
                                        <div className={cx('layout-pdf-actions')}>
                                            <OutlinedButton
                                                title={isLayoutJustCreated ? 'Back' : 'Back to edit'}
                                                onClick={() => {
                                                    if (isLayoutJustCreated) {
                                                        setIsAddEstimateLayout(false);
                                                        setViewLayoutPdf(false);
                                                        setLayoutJustCreated(false);
                                                        onLayoutCreatedView?.(false);
                                                    } else {
                                                        setViewLayoutPdf(false);
                                                    }
                                                }}
                                                className={cx('back-from-pdf-button')}
                                            />
                                            <PrimaryButton
                                                title="Export PDF"
                                                onClick={() => layoutPdfRef.current?.exportPdf?.()}
                                                className={cx('continue-button')}
                                            />
                                        </div>
                                        <div className={cx('layout-pdf-wrapper')}>
                                            <EstimatePDF
                                                data={getAggregatedLayoutFormData()}
                                                ref={layoutPdfRef}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {renderStepContent()}
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={cx('list-estimate-layout-content')}>
                            {estimateLayouts.map((estimateLayout) => (
                                <div className={cx('estimate-layout-item')} key={estimateLayout.id}>
                                    <h3 className={cx('estimate-layout-item-title')}>{estimateLayout.estimateLayoutName}</h3>
                                    <p className={cx('estimate-layout-item-description')}>{estimateLayout.aboutUs}</p>
                                    <p className={cx('estimate-layout-item-last-updated')}>
                                        Last updated: {estimateLayout.updatedAt ? dayjs(estimateLayout.updatedAt).fromNow() : '-'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            <BaseModalV2
                open={showModalCreateEstimateLayout}
                onClose={handleCloseModalCreateEstimateLayout}
                title="Create Estimate Layout"
                width="90%"
                maxWidth="600px"
            >
                <div className={cx('modal-create-estimate-layout-content')}>
                    <div className={cx('input-container')}>
                        <label className={cx('input-label')}>Estimate Layout Name</label>
                        <PrimaryInput
                            name="estimateLayoutName"
                            placeholder="Enter Estimate Layout Name"
                            value={formData.estimateLayoutName}
                            onChange={(e) => handleChange('estimateLayoutName', e.target.value ?? '')}
                            errorMessage={error.estimateLayoutName}
                            className={cx('input-field', { error: error.estimateLayoutName })}
                        />
                    </div>

                    <div className={cx('input-container')}>
                        <label className={cx('input-label')}>Location</label>
                        <PrimarySelectInput
                            name="location"
                            options={[
                                { label: 'Christian Brother Roofing', value: 'Christian Brother Roofing' },
                            ]}
                            value={formData.location}
                            onChange={(value) => handleChange('location', value ?? '')}
                            errorMessage={error.location}
                            className={cx('select-input', { error: error.location })}
                        />
                    </div>
                </div>

                <div className={cx('modal-actions')}>
                    <PrimaryButton
                        title="Cancel"
                        onClick={handleCloseModalCreateEstimateLayout}
                        className={cx('cancel-button')}
                    />
                    <PrimaryButton
                        title="Create Layout"
                        onClick={() => handleCreateEstimateLayout()}
                        className={cx('create-button')}
                    />
                </div>
            </BaseModalV2>
        </div>
    );
});

EstimateLayout.displayName = "EstimateLayout";

export default EstimateLayout;